import React from 'react';
import {
    Box,
    HStack,
    Container,
    Flex,
    Center,
    Text,
    Image,
    Tooltip,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Icon,
    useColorModeValue,
    Badge,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdLogout } from 'react-icons/md';
import { useAuth } from '../Utils/AuthContext';
import { getInitials } from '../Utils/Helpers';
import ThemeToggle from './ThemeToggle';
import { useCompany } from '../Utils/CompanyContext';

/**
 * Modern glassmorphic Navbar for digital identity platform.
 * Supports light, dark, and system themes with executive branding.
 */
const Navbar: React.FC = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { company } = useCompany();

    const navBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(7, 12, 24, 0.85)');
    const navBorder = useColorModeValue('rgba(13, 31, 76, 0.08)', 'rgba(255, 255, 255, 0.08)');
    const textColor = useColorModeValue('#0F172A', '#F8FAFC');
    const menuBg = useColorModeValue('#FFFFFF', '#0D1527');
    const menuBorder = useColorModeValue('rgba(13, 31, 76, 0.1)', 'rgba(255, 255, 255, 0.1)');
    const avatarBorder = useColorModeValue('white', '#0D1527');
    const avatarFallbackBg = useColorModeValue('#E8EEFA', '#16233F');
    const avatarFallbackColor = useColorModeValue('#0A3B82', '#60A5FA');
    const logoBg = useColorModeValue('white', 'rgba(255, 255, 255, 0.96)');
    const logoBorder = useColorModeValue('rgba(0, 0, 0, 0.04)', 'transparent');
    const userHovBg = useColorModeValue('blackAlpha.050', 'whiteAlpha.050');
    const emailColor = useColorModeValue('gray.500', 'gray.400');
    const logoutHoverBg = useColorModeValue('red.50', 'whiteAlpha.100');

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const handleBrandClick = () => {
        if (user?.role === 'Supervisor' || user?.role === 'Admin') {
            navigate('/supervisor');
        } else {
            navigate('/dashboard');
        }
    };

    return (
        <Box 
            as="nav"
            bg={navBg} 
            backdropFilter="blur(16px)"
            borderBottom="1px solid" 
            borderColor={navBorder} 
            position="sticky" 
            top={0} 
            zIndex={100}
            transition="background-color 0.25s ease, border-color 0.25s ease"
        >
            <Container maxW="1180px">
                <Flex h={{ base: "64px", md: "74px" }} align="center" justify="space-between">
                    
                    {/* Brand / Logo */}
                    <HStack 
                        spacing={3} 
                        cursor="pointer" 
                        onClick={handleBrandClick}
                        _hover={{ opacity: 0.88, transform: 'scale(0.99)' }}
                        transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                    >
                        <Box
                            p={{ base: "4px 8px", md: "5px 12px" }}
                            bg={logoBg}
                            borderRadius="10px"
                            boxShadow="0 2px 8px rgba(0, 0, 0, 0.06)"
                            border="1px solid"
                            borderColor={logoBorder}
                        >
                            <Image 
                                src={company.logo} 
                                h={{ base: "22px", md: "28px" }} 
                                objectFit="contain" 
                                alt={`${company.companyName} Logo`}
                            />
                        </Box>
                    </HStack>

                    {/* Right utilities: Theme Switcher & User Profile */}
                    <HStack spacing={{ base: 2, sm: 4, md: 5 }}>
                        
                        {/* Theme Toggle (Light / Dark / System) */}
                        <ThemeToggle variant="segmented" size="sm" />

                        {/* User Profile Menu */}
                        <Menu gutter={8} placement="bottom-end">
                            <MenuButton 
                                as={Box} 
                                cursor="pointer"
                                _hover={{ opacity: 0.9 }}
                                transition="all 0.2s"
                            >
                                <HStack spacing={3} p="4px 8px" borderRadius="12px" _hover={{ bg: userHovBg }}>
                                    <Tooltip label={user?.name || 'Account'} placement="bottom-end" hasArrow>
                                        <Center 
                                            w={{ base: "36px", md: "40px" }} 
                                            h={{ base: "36px", md: "40px" }} 
                                            borderRadius="full" 
                                            bg={avatarFallbackBg} 
                                            flexShrink={0}
                                            border="2px solid"
                                            borderColor={avatarBorder}
                                            boxShadow="0 2px 8px rgba(0, 0, 0, 0.12)"
                                            overflow="hidden"
                                        >
                                            {user?.photoUrl ? (
                                                <Image 
                                                    src={user.photoUrl} 
                                                    w="full" 
                                                    h="full" 
                                                    objectFit="cover" 
                                                    alt={user.name}
                                                />
                                            ) : (
                                                <Text fontSize={{ base: "12px", md: "13px" }} fontWeight={700} color={avatarFallbackColor}>
                                                    {getInitials(user?.name ?? 'U')}
                                                </Text>
                                            )}
                                        </Center>
                                    </Tooltip>
                                    
                                    <Box display={{ base: 'none', md: 'block' }} textAlign="left">
                                        <HStack spacing={2}>
                                            <Text fontSize="13px" fontWeight={600} color={textColor} lineHeight="1.2">
                                                {user?.name}
                                            </Text>
                                            {user?.role && (
                                                <Badge
                                                    fontSize="9px"
                                                    fontWeight={700}
                                                    letterSpacing="0.06em"
                                                    borderRadius="full"
                                                    px={2}
                                                    py="1px"
                                                    bg={user.role === 'Supervisor' ? 'brand.500' : 'accentCyan.500'}
                                                    color="white"
                                                    textTransform="uppercase"
                                                >
                                                    {user.role}
                                                </Badge>
                                            )}
                                        </HStack>
                                        <Text 
                                            fontSize="11px" 
                                            color={emailColor} 
                                            letterSpacing="0.02em" 
                                            mt={0.5}
                                        >
                                            {user?.email || company.companyName}
                                        </Text>
                                    </Box>
                                </HStack>
                            </MenuButton>

                            <MenuList 
                                bg={menuBg}
                                borderColor={menuBorder}
                                borderRadius="14px" 
                                boxShadow="0 20px 40px -15px rgba(0, 0, 0, 0.3)"
                                p={1.5}
                                minW="180px"
                                zIndex={1100}
                            >
                                <Box px={3} py={2} borderBottom="1px solid" borderColor={menuBorder} mb={1}>
                                    <Text fontSize="12px" fontWeight={600} color={textColor}>
                                        {user?.name}
                                    </Text>
                                    <Text fontSize="11px" color={emailColor}>
                                        {user?.email}
                                    </Text>
                                </Box>
                                <MenuItem 
                                    onClick={handleLogout}
                                    icon={<Icon as={MdLogout as any} boxSize="16px" />}
                                    borderRadius="8px"
                                    fontSize="13px"
                                    fontWeight={500}
                                    color="red.500"
                                    _hover={{ bg: logoutHoverBg }}
                                    h="40px"
                                >
                                    Log Out
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </HStack>

                </Flex>
            </Container>
        </Box>
    );
};

export default Navbar;
